import useIntl from '@/utils/intl'
import React, { FunctionComponent, useState } from 'react'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import Image from 'next/image'
import { ArrowForwardIos } from '@mui/icons-material'
import { IMG_URL, resolveImageUrl } from '@/utils/constants'
import { useTheme } from '@/context/ThemeContext'

interface Props {
  amount?: number
  onContinue: () => void
  phoneNumber?: string
  onTermsClick: () => void
}

const LeasingInfo: FunctionComponent<Props> = ({
  amount,
  onContinue,
  onTermsClick
}) => {
  const { translate } = useIntl()
  const [show, setShow] = useState(true)
  const { theme } = useTheme()

  const displayAmount =
    amount !== undefined ? (!show ? `${amount}₮` : '---,---₮') : '---,---₮'

  return (
    <div className='flex flex-col h-screen w-full bg-surfaceInverse'>
      <div className='flex-grow overflow-y-auto px-4 py-4 sm:px-8 sm:py-8 flex flex-col gap-4'>
        <div className='rounded-2xl w-full flex flex-col justify-between border'>
          <div
            className='rounded-2xl w-full p-4 flex flex-col justify-between border bg-no-repeat bg-cover bg-center space-y-4 bg-[#2E353B] '
            style={{ backgroundImage: `url(${IMG_URL}/uploads/leasing_card.png)` }}
          >
            <div className='flex justify-between items-center w-full'>
              <Image
                draggable={false}
                width={58}
                height={32}
                src={resolveImageUrl('/uploads/icons/mobicom_logo_grey.svg')}
                alt='Mobicom logo'
                className='w-16 pt-2'
              />
              <div className='border p-2 rounded-2xl bg-white/50 inline-flex items-center justify-center'>
                <button
                  onClick={() => setShow(!show)}
                  className='focus:outline-none'
                  aria-label={show ? 'Hide amount' : 'Show amount'}
                >
                  {show
                    ? <FaEyeSlash className='w-5 h-5 text-textHigh' />
                    : <FaEye className='w-5 h-5 text-textHigh' />}
                </button>
              </div>
            </div>
            <div className='flex flex-col space-y-1'>
              <div className='flex justify-between items-center w-full mt-4'>
                <div className='text-base font-bold text-white'>{displayAmount}</div>
                <div className='text-white text-base font-bold whitespace-nowrap'>
                  --/--/--
                </div>
              </div>
            </div>
            <div className='flex justify-between items-center w-full'>
              <div className='text-white text-sm'>
                {translate('leasing.info.monthPay')}
              </div>
              <div className='text-white text-sm'>
                {translate('leasing.info.date')}
              </div>
            </div>
          </div>
        </div>
        <div className='border border-primary rounded-2xl w-full p-4 flex flex-col justify-start items-start mt-4'>
          <div className='flex items-start space-x-3'>
            <Image
              draggable={false}
              width={24}
              height={24}
              alt='benefit icon'
              src={resolveImageUrl('/uploads/icons/star_icon.svg')}
              className='w-5 h-5 mt-1'
            />
            <div className='flex-1'>
              <div className='text-base text-textHigh font-medium'>
                {translate('leasing.info.benefit')}
              </div>
              <div className='text-sm text-textMid pt-2'>
                {translate('leasing.info.benefit1')}
              </div>
              <div className='text-base text-textHigh pt-4'>
                {translate('leasing.info.benefit_terms')}
              </div>
              <ul className='list-disc list-inside pt-1 space-y-1'>
                <li className='text-textMid text-sm'>
                  {translate('leasing.info.benefit_terms1')}
                </li>
                <li className='text-textMid text-sm'>
                  {translate('leasing.info.benefit_terms2')}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div onClick={onTermsClick} className='w-full mt-4'>
          <div className='border border-primary rounded-2xl px-4 py-3 flex justify-between items-center cursor-pointer hover:opacity-80'>
            <div className='flex items-center space-x-2'>
              <img
                src={resolveImageUrl(`/uploads/icons/${
                        theme === 'dark'
                          ? 'file_dark.svg'
                          : 'file_light.svg'
                      }`)}
                alt='Tailing Icon'
                className='w-5 h-5 object-contain'
                draggable='false'
              />
              <div className='text-textHigh text-base'>
                {translate('leasing.info.termsofservice')}
              </div>
            </div>
            <ArrowForwardIos className='text-textMid text-sm' />
          </div>
        </div>
      </div>
      <div className='sticky bottom-0 rounded-b-xl right-0 left-0 z-50 md:p-6 p-4 border-t bg-surfaceInverse border-secondaryMidStroke'>
        <div className='md:w-96 w-full'>
          <DarkButton fullwidth onClick={onContinue}>
            {translate('continue')}
          </DarkButton>
        </div>
      </div>
    </div>
  )
}

export default LeasingInfo
